import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

/**
 * The Alumnia UI 1.2.1 badge (docs/redesign/sistema/Badge.md), reached with
 * `look="encuentro"`: a short state or type label in `caption`, the tone next
 * to the word. It wraps instead of clipping when the text is at 200 %.
 */
const encuentroBadgeVariants = cva(
  "inline-flex min-h-6 max-w-full items-center gap-1 rounded-en-sm px-2 py-0.5 font-en-sans text-en-caption [overflow-wrap:anywhere] en-large-text:hyphens-auto [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5",
  {
    variants: {
      tone: {
        neutral: 'bg-en-surface-sunken text-en-ink',
        positive: 'bg-en-positive-soft text-en-positive',
        warning: 'bg-en-warning-soft text-en-warning',
        danger: 'bg-en-danger-soft text-en-danger',
        accent: 'bg-en-accent-soft text-en-ink',
        highlight: 'bg-en-highlight text-en-ink',
        electric: 'bg-en-electric-soft text-en-electric',
        spark: 'bg-en-spark-soft text-en-ink',
      },
      onPhoto: {
        true: 'min-h-7 rounded-en-full bg-en-surface px-2.5 text-en-ink shadow-en-card',
        false: '',
      },
    },
    defaultVariants: { tone: 'neutral', onPhoto: false },
  }
)

type EncuentroBadgeTone = NonNullable<VariantProps<typeof encuentroBadgeVariants>['tone']>

type ClassicBadgeProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof badgeVariants> & { look?: 'classic' }

type EncuentroBadgeProps = React.HTMLAttributes<HTMLSpanElement> & {
  look: 'encuentro'
  /** Neutral by default. `electric` marks verified, AI or "Actual"; `spark` is social. */
  tone?: EncuentroBadgeTone
  /** White pill with a shadow over a photo, such as Presencial or Online. */
  onPhoto?: boolean
}

export type BadgeProps = ClassicBadgeProps | EncuentroBadgeProps

function Badge(props: BadgeProps) {
  if (props.look === 'encuentro') {
    const { className, tone, onPhoto, look: _look, ...rest } = props
    return (
      <span
        data-slot="badge"
        data-look="encuentro"
        className={cn(encuentroBadgeVariants({ tone, onPhoto }), className)}
        {...rest}
      />
    )
  }
  const { className, variant, look: _look, ...rest } = props
  return <div className={cn(badgeVariants({ variant }), className)} {...rest} />
}

type CountProps = Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> & {
  /** Nothing renders at 0 or below: the system shows no zero counters. */
  value: number
  /** Above this the counter reads "99+". */
  max?: number
  /** Screen-reader text after the number, such as " sin leer". Omit when the parent names it. */
  srLabel?: string
}

/**
 * Counter (`a-count`): `spark-strong` with a white number (4.6:1). Only for
 * what needs action: messages, notifications and approvals. Encuentro only.
 */
function Count({ value, max = 99, srLabel, className, ...props }: CountProps) {
  if (!Number.isFinite(value) || value <= 0) return null
  return (
    <span
      data-slot="count"
      className={cn(
        'inline-grid min-h-5 min-w-5 place-items-center whitespace-nowrap [overflow-wrap:normal] rounded-en-full bg-en-spark-strong px-1.5 font-en-sans text-en-caption leading-none font-bold tracking-normal text-en-spark-strong-ink tabular-nums',
        className
      )}
      {...props}
    >
      {value > max ? `${max}+` : value}
      {srLabel && <span className="sr-only">{srLabel}</span>}
    </span>
  )
}

type UnreadDotProps = Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> & {
  /** Accessible name ("Sin leer"). Without it the dot is decorative. */
  label?: string
}

/** Unread (`a-unread-dot`): an `electric` dot. Not a counter. Encuentro only. */
function UnreadDot({ label, className, ...props }: UnreadDotProps) {
  return (
    <span
      data-slot="unread-dot"
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      className={cn('inline-block size-2 shrink-0 rounded-en-full bg-en-electric', className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants, encuentroBadgeVariants, Count, UnreadDot }
export type { EncuentroBadgeTone }
